import React from 'react';
import { useAppStateContext } from '../../../hooks/useAppState';
import JitterBoxPlot from '../../plots/JitterBoxPlot';

export default function WQPollutantRemovalPanel() {
  const { state } = useAppStateContext();
  const { selectedAnalytes } = state.wq;



  if (!selectedAnalytes || selectedAnalytes.length === 0) {
    return (
      <div className="flex flex-col gap-8 my-8 w-full min-h-[440px] py-8">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-4">Pollutant Reduction Analysis</h3>
          <p className="text-gray-600 mb-8">
            No analytes selected. Please select analytes in the Performance Index tab to view pollutant removal analysis.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 my-8 w-full min-h-[440px] py-8">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mb-4">Pollutant Reduction Analysis</h3>
        <p className="text-gray-600">
          Box plots showing pollutant removal performance for selected analytes.
        </p>
      </div>

      {/* Deconstruct the entries of the state wq indexData to the key (analyte) and extract the plot_data, which is all we need for the boxplot */}
      {Object.entries(state.wq.indexData).map(( [analyteName, { plot_data }] ) => {

        // That plot_data is an array of objects, so we can use it directly in the boxplot
        console.assert(Array.isArray(plot_data), `Expected plot_data to be an array for analyte ${analyteName}`);

        let allPlotData = state.wq.comparisonMode ? plot_data : plot_data.filter(d => d.is_comparison !== 'true');

        // I think I'll want to make the API return the emc_percent_reduction but for now, we can calculate it here
        allPlotData = allPlotData.map((d) => {
            return {...d, emc_percent_reduction : ((d.inflow_emc - d.outflow_emc) / d.inflow_emc) }
        })


        let boxPlotData = {};
        
        // loop over the unique BMPs and create an array of objects for each BMP
        // the unique BMPs should really just be the selected BMP and the comparison data
        Array.from(new Set( allPlotData.map((d) =>  d.bmp ) )).forEach((bmp) => {
          boxPlotData[bmp] = allPlotData.filter((d) => d.bmp === bmp) ;
        });

        
        if (!boxPlotData || boxPlotData.length === 0) {
          return (
            <div key={analyteName} className="bg-gray-50 border rounded-lg p-6">
              <h4 className="text-md font-semibold mb-2">{analyteName}</h4>
              <p className="text-gray-500">No data available for this analyte.</p>
            </div>
          );
        }

        console.log("boxPlotData", boxPlotData)

        return (
          <div key={analyteName} className="bg-white border rounded-lg p-6">
            <h4 className="text-lg font-semibold mb-4">{analyteName}</h4>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {/* EMC Reduction */}
              <div className="bg-gray-50 p-3 md:p-4 rounded">
                <h5 className="text-sm md:text-md font-medium mb-3">% EMC Reduction</h5>
                <JitterBoxPlot 
                  data={boxPlotData}
                  accessors={['emc_percent_reduction']}
                  yLabel="% Reduction"
                  height={350}
                  referenceLine={0}
                  colors={['#0299ad','#329da8']}
                />
              </div>

              {/* Mass Load Reduction */}
              {/* It was requested to remove problematic records (outflow > inflow) from this plot */}
              <div className="bg-gray-50 p-3 md:p-4 rounded">
                <h5 className="text-sm md:text-md font-medium mb-3">% Mass Load Reduction</h5>
                <JitterBoxPlot 
                  data={Object.fromEntries(Object.entries(boxPlotData).map(([k,v]) => [k, v.filter(d => d.outflow_vol_liters <= d.inflow_vol_liters)]))}
                  accessors = {['mass_reduction_efficiency']}
                  yLabel="% Reduction"
                  height={350}
                  referenceLine={0}
                  colors={['#0299ad','#329da8']}
                />
              </div>

              {/* EMC Data */}
              <div className="bg-gray-50 p-3 md:p-4 rounded">
                <h5 className="text-sm md:text-md font-medium mb-3">EMC Data</h5>
                <JitterBoxPlot 
                  data={boxPlotData}
                  accessors={['inflow_emc', 'outflow_emc']}
                  legendLabels={['Influent EMC', 'Effluent EMC']}
                  height={350}
                  margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
                  yLabel={`EMC (${(Object.values(boxPlotData).find(v => v.length)?.[0].units) || ""})`}
                  colors={['#3182ce', '#e53e3e']}
                />
              </div>

              {/* Mass Load Data */}
              {/* It was requested to remove problematic records (outflow > inflow) from this plot */}
              <div className="bg-gray-50 p-3 md:p-4 rounded">
                <h5 className="text-sm md:text-md font-medium mb-3">Mass Load Data</h5>
                <JitterBoxPlot 
                  data={Object.fromEntries(Object.entries(boxPlotData).map(([k,v]) => [k, v.filter(d => d.outflow_vol_liters <= d.inflow_vol_liters)]))}
                  accessors={['inflow_mass', 'outflow_mass']}
                  legendLabels={['Influent Mass Load', 'Effluent Mass Load']}
                  height={350}
                  margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
                  yLabel={`Mass Load (${(Object.values(boxPlotData).find(v => v.length)?.[0].units).replace(/\/.*/, "") || ""})`}
                  colors={['#3182ce', '#e53e3e']}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
} 